// Login.tsx (FIXED - Tenant login working + same as old behavior)

import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { LoginService, OpenAPI, OrganizationUserRolesEnum } from "@propertelligent/client-api";
import { TopBar } from "../common/TopBar";
import { MyContext } from "../../utils/context/LoginContext";
import { UserLoginData } from "../common/charts/Enums";
import { URLS, USER_ROLES, USERS } from "../../constants/constants";
import { GetImages } from "../../utils/GetImages";
import "./LoginStyles.css";
import apiHelper from "../../utils/apiHelper";
import AddButtonWithArrow from "../firstTimeUserExperience/commonCom/AddButtonWithArrow";
import OtpInput from "./OtpInput";
import InputField from "../../newComponents/form-fields/InputField";
import EmailInput from "../../newComponents/form-fields/EmailInput";
import MobileAuthLink from "./MobileAuthLink";
import { usePermissions } from "../../hooks/usePermissions";
import { Spinner } from "react-bootstrap";

interface LoginFormValues {
  email: string;
  password: string;
}

export const Login: React.FC = () => {
  const { setIsLogin, setUserRole } = useContext(MyContext);
  const { refreshPermissions } = usePermissions();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [loading, setLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false); // For OTP screen
  const [codeError, setCodeError] = useState(false);
  const [code, setCode] = useState("");
  const [isPropertyNotAssigned, setIsPropertyNotAssigned] = useState(false);
  const [comingFromSignUp, setComingFromSignUp] = useState(false);
  const [comingFromLogout, setComingFromLogout] = useState(false);

  const existingToken = sessionStorage.getItem(UserLoginData.token);

  const methods = useForm<LoginFormValues>({
    mode: "onBlur",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { handleSubmit, watch } = methods;
  const values = watch();

  useEffect(() => {
    if (state?.isComingFromSignUP) setComingFromSignUp(true);
    if (state?.isComingFromLogout) setComingFromLogout(true);

    const timer = setTimeout(() => {
      setComingFromSignUp(false);
      setComingFromLogout(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [state]);

  // Handle OTP Submit
  const handleCodeInputSubmit = async (otpCode: string) => {
    if (otpCode.length === 6) {
      setLoading(true);
      setCodeError(false);

      apiHelper(
        LoginService.putApiLoginVerifyEmail,
        { showNotification: false },
        { email: values.email, code: otpCode }
      )
        .then((res: any) => {
          if (res.data.success) {
            const data = res.data.success;

            // Save all session data
            sessionStorage.setItem(UserLoginData.token, data.token);
            OpenAPI.TOKEN = data.token;
            sessionStorage.setItem(UserLoginData.user_name, `${data.firstName} ${data.lastName}`);
            sessionStorage.setItem(UserLoginData.user_id.toString(), data.userId);
            sessionStorage.setItem("actualUserId", data.userId?.toString() || "");
            sessionStorage.setItem(UserLoginData.organization_id, data.organizationUserInfo?.organizationId || "");
            sessionStorage.setItem(UserLoginData.currency_symbol, data.currencySymbol || "");
            const isStaffMember = data.organizationUserInfo?.organizationUserRoleId &&
              data.organizationUserInfo?.organizationUserRoleId !== OrganizationUserRolesEnum.Admin;
            const finalOnboarded = isStaffMember ? "true" : (data.userOnboarded?.toString() || "false");
            sessionStorage.setItem("userOnboarded", finalOnboarded);
            sessionStorage.setItem("userRoleId", data.userRoleId?.toString() || "");
            sessionStorage.setItem(
              UserLoginData.permissions,
              JSON.stringify(data.organizationUserInfo?.permissions || {})
            );
            refreshPermissions();

            // ---- required from old login ----
            if (USER_ROLES[data.userRoleId] === USERS.TENANT) {
              sessionStorage.setItem("tenantInfo", JSON.stringify(data.tenantInfo || {}));
              sessionStorage.setItem(UserLoginData.tenant_id, data.tenantInfo?.tenantId?.toString() || "");
              sessionStorage.setItem(UserLoginData.user_id, data.tenantInfo?.unitId?.toString() || ""); // Maybe `user_id` is `unitId` for tenants (not sure but old code had this so i kept it and it works) - sahbaz
            }


            if (USER_ROLES[data.userRoleId] === USERS.TENANT && data.tenantInfo) {
              sessionStorage.setItem("tenantInfo", JSON.stringify(data.tenantInfo));
            } else if (USER_ROLES[data.userRoleId] === USERS.CONTRACTOR && data.contractorInfo) {
              sessionStorage.setItem("contractorInfo", JSON.stringify(data.contractorInfo));
            }

            setIsLogin(true);
            navigate(URLS.HOME);
          }
        })
        .catch(() => {
          setCodeError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // Login Submit
  const onSubmit = async (data: LoginFormValues) => {
    setLoading(true);

    try {
      const response = await apiHelper(LoginService.postApiLogin, {
        successMessage: "Login successful",
        failureMessage: "Incorrect email or password",
        showNotification: true,
      }, data);

      const res = response.data;

      if (!res.success.isEmailVerified) {
        setLoggedIn(true); // Show OTP screen
        
        return;
      }

      // Email already verified → direct login
      const success = res.success;
      sessionStorage.setItem(UserLoginData.token, success.token);
      OpenAPI.TOKEN = success.token;
      sessionStorage.setItem(UserLoginData.user_name, `${success.firstName} ${success.lastName}`);
      sessionStorage.setItem(UserLoginData.user_id.toString(), success.userId);
      sessionStorage.setItem("actualUserId", success.userId?.toString() || "");
      sessionStorage.setItem(UserLoginData.organization_id, success.organizationUserInfo?.organizationId || "");
      sessionStorage.setItem(UserLoginData.currency_symbol, success.currencySymbol || "");
      const isStaffMember = success.organizationUserInfo?.organizationUserRoleId &&
        success.organizationUserInfo?.organizationUserRoleId !== OrganizationUserRolesEnum.Admin;
      const finalOnboarded = isStaffMember ? "true" : (success.userOnboarded?.toString() || "false");
      sessionStorage.setItem("userOnboarded", finalOnboarded);
      sessionStorage.setItem("userRoleId", success.userRoleId?.toString() || "");
      sessionStorage.setItem("organizationUserId", success?.organizationUserInfo?.organizationUserId?.toString() || "");
      sessionStorage.setItem(
        UserLoginData.permissions,
        JSON.stringify(success?.organizationUserInfo?.permissions || {})
      );
      refreshPermissions();

      const role = USER_ROLES[success.userRoleId];
      setUserRole(role);
      setIsLogin(true);

      // Critical Fix: Tenant property check (same as old file)
      if (role === USERS.TENANT) {
        // ---- required from old login ----
        sessionStorage.setItem("tenantInfo", JSON.stringify(success.tenantInfo || {}));
        sessionStorage.setItem(UserLoginData.tenant_id, success.tenantInfo?.tenantId?.toString() || "");
        sessionStorage.setItem(UserLoginData.user_id, success.tenantInfo?.unitId?.toString() || ""); // Maybe `user_id` is `unitId` for tenants (i am not sure but old code had this so i kept it and it works) - sahbaz



        sessionStorage.setItem("tenantInfo", JSON.stringify(success.tenantInfo || {}));

        // Yeh line sabse important hai — purani file jaisa safe check
        if (!success.tenantInfo?.propertyId) {
          setIsPropertyNotAssigned(true);
          return; // Don't navigate yet
        }
      } else if (role === USERS.CONTRACTOR) {
        sessionStorage.setItem("contractorInfo", JSON.stringify(success.contractorInfo || {}));
      }

      // Agar property assigned hai ya non-tenant hai → home pe jao
      navigate(URLS.HOME);

    } catch (error) {
      // apiHelper already shows toast
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    apiHelper(LoginService.putApiLoginResendEmailVerificationCode, {
      showNotification: true,
      successMessage: "Verification code sent again",
      failureMessage: "Failed to resend code",
    }, values.email);
  };

  const logoutFunction = () => {
    apiHelper(LoginService.putApiLoginLogout, {
      successMessage: "Logged out successfully",
      showNotification: true,
    }).finally(() => {
      sessionStorage.clear();
      refreshPermissions();
      setIsLogin(false);
      setIsPropertyNotAssigned(false);
      navigate(URLS.HOME);
    });
  };
  
  return (
    <>
      <div className="background-section background-image login-page-container">
        <TopBar
          haveAccount="Don’t have an account?"
          signInUp="Sign up"
          isPropertyNotAssigned={isPropertyNotAssigned}
        />

        <div className="form-container-main login-padding">
          {isPropertyNotAssigned ? (
            <div className="loginFormWrapper">
              <div className="onholdWrapper text-center p-3">
                <img src={GetImages.onHold} alt="on hold" />
                <p className="normal_text grey_subtext1 mt-3">
                  You Haven’t Been Assigned to Any Property or Unit Yet By Your Landlord
                </p>
                <p className="normal_text fw-600 blue mt-3 clickable" onClick={logoutFunction}>
                  Log Out
                </p>
              </div>
            </div>
          ) : loggedIn ? (
            /* OTP Screen */
            <div className="codeVerifier">
              <p className="top_header black mb-2">2-Step Verification</p>
              <p className="normal_text fw-500 grey_subtext1 mb-4">
                Please enter the 6-digit verification code sent to <strong>{values.email}</strong>
              </p>

              <div style={{ marginBottom: codeError ? "8px" : "16px" }}>
                <OtpInput
                  code={code}
                  setCode={setCode}
                  status={loggedIn}
                  handleCodeInputChange={handleCodeInputSubmit}
                  comingFrom="login"
                />
              </div>

              {codeError && (
                <p className="errorMessageStyles" style={{ marginBottom: "16px" }}>
                  Incorrect code. Please try again.
                </p>
              )}

              <div className="resendPasswordBox">
                <p className="normal_text grey_subtext1 fw-500">
                  Didn’t receive it?{" "}
                  <span className="resendCodeText normal_text blue" onClick={handleResendCode}>
                    Resend code
                  </span>
                </p>
              </div>

              <div className="buttonContaier">
                <AddButtonWithArrow
                  onClick={() => setLoggedIn(false)}
                  className="goBackButton normal_text fw-600"
                  disabled={loading}
                  type="button"
                  buttonname="Back"
                />
                <AddButtonWithArrow
                  className="goBackButton normal_text fw-600"
                  disabled={loading || code.length !== 6}
                  onClick={() => handleCodeInputSubmit(code)}
                  type="button"
                  buttonname="Verify Code"
                  loading={loading}
                />
              </div>
            </div>
          ) : !existingToken ? (
            /* Login Form */
            <div className="loginFormWrapper">
              <p className="login_header black mb-4">Log in</p>

              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <EmailInput
                    name="email"
                    label="Email"
                    required
                    placeholder="Email Address"
                    requiredMessage="Email is required"
                  />

                  <div className="mt-3">
                    <InputField
                      name="password"
                      label="Password"
                      type="password"
                      required
                      allowSpecialChars
                      requiredMessage="Password is required"
                      placeholder="Enter your password"
                    />
                  </div>

                  <div className="rememberAndForgetWrapper mt-3">
                    <div className="rememberMeBox">
                    </div>
                    <p
                      className="normal_text fw-600 blue cursor-pointer"
                      onClick={() => navigate(URLS.FORGET_PASSWORD)}
                    >
                      Forgot Password?
                    </p>
                  </div>

                  <div className="mt-4">
                    <AddButtonWithArrow
                      className="loginButton sub_heading w-100"
                      type="submit"
                      buttonname="Log In"
                      disabled={loading}
                      loading={loading}
                    />
                  </div>

                  {/* Mobile Only Signup Link */}
                  <MobileAuthLink variant="signup" />
                </form>
              </FormProvider>
            </div>

          ) : <div className="loader-container">
            <div className="loader">Loading...</div>
          </div>}
        </div>
      </div>
    </>
  );
};